import { useCallback, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUserOnboarding, useUserSession } from "shared/contexts/UserContext";
import { useTranslation } from "shared/hooks/useTranslation";

export default function FirstUseOnboarding() {
    const { t, getPathWithLanguage } = useTranslation();
    const navigate = useNavigate();
    const { profile, isLogged } = useUserSession();
    const { onboardingReady, onboardingCompleted, setOnboardingCompleted } = useUserOnboarding();
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setOpen(onboardingReady && !onboardingCompleted && isLogged());
    }, [isLogged, onboardingCompleted, onboardingReady]);

    const finish = useCallback(async () => {
        setSaving(true);
        try {
            await setOnboardingCompleted(true);
            setOpen(false);
        } finally {
            setSaving(false);
        }
    }, [setOnboardingCompleted]);

    const start = useCallback(async (destination: "movement" | "group") => {
        await finish();
        const base = `/${profile?.username ?? ""}`;
        const path = destination === "movement"
            ? `${base}/personal?action=add`
            : `${base}/groups?action=create`;
        navigate(getPathWithLanguage(path));
    }, [finish, getPathWithLanguage, navigate, profile?.username]);

    return (
        <Dialog
            open={open}
            onClose={() => { if (!saving) void finish(); }}
            aria-labelledby="first-use-title"
            aria-describedby="first-use-description"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="first-use-title" sx={{ pb: 1, fontSize: 24, fontWeight: 800 }}>
                {t("onboarding.welcomeTitle")}
            </DialogTitle>
            <DialogContent>
                <Typography id="first-use-description" color="text.secondary" sx={{ mb: 1 }}>
                    {t("onboarding.welcomeBody")}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: 2 }}>
                    <Button fullWidth variant="contained" onClick={() => void start("movement")} disabled={saving}>
                        {t("onboarding.addExpense")}
                    </Button>
                    <Button fullWidth variant="outlined" onClick={() => void start("group")} disabled={saving}>
                        {t("onboarding.createGroup")}
                    </Button>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={() => void finish()} disabled={saving}>
                    {t("onboarding.skip")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
